import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import GridTable from '../common/GridTable/GridTable'
import SortButton from '../common/buttons/SortButton'
import { getNextSortValue } from '../../utility/tableSorting'

import classes from './OrganizationsTable.module.scss'

const COLUMN_WIDTH = 'minmax(140px, 1fr)'

const OrganizationsTable = ({
  organizations, organizationsCount, hasMore, isLoading = false, isSortLoading = false, sortOption, onSort, onFetchMore, onRowClick,
  selectedOrganizationId,
}) => {
  const { t } = useTranslation()

  const firstColumn = {
    width: '280px',
    header: (
      <SortButton
        label={t('organizations.page.organizationsCount', { count: organizationsCount })}
        active={sortOption.sortField === 'name' && sortOption.sortValue !== null}
        onClick={() => onSort('name', getNextSortValue(sortOption, 'name'))}
        loading={isSortLoading && sortOption.sortField === 'name'}
      />
    ),
    render: (organization) => <span className='font-weight-bold'>{organization.name}</span>,
    skeleton: <div data-skeleton='name' />,
  }

  const columns = [
    {
      key: 'created_at', label: t('organizations.table.dateCreated'),
      render: (organization) => moment(organization.createdAt).format('MMM D, YYYY'),
    },
    { key: 'coaches_count', label: t('organizations.table.coaches'), render: (organization) => organization.coachesCount },
    {
      key: 'certified_coaches_count', label: t('organizations.table.certifiedCoaches'),
      render: (organization) => organization.certifiedCoachesCount,
    },
    { key: 'communities_count', label: t('organizations.table.communities'), render: (organization) => organization.communitiesCount },
  ]

  return (
    <GridTable
      id='organizationsTable'
      className={classes.table}
      firstColumn={firstColumn}
      columns={columns.map(column => ({
        key: column.key,
        gridWidth: COLUMN_WIDTH,
        header: (
          <SortButton
            label={column.label}
            active={sortOption.sortField === column.key && sortOption.sortValue !== null}
            onClick={() => onSort(column.key, getNextSortValue(sortOption, column.key))}
            loading={isSortLoading && sortOption.sortField === column.key}
          />
        ),
        render: column.render,
        skeleton: <div data-skeleton='value' />,
      }))}
      rows={organizations}
      rowKey={(organization) => organization.id}
      hasMore={hasMore}
      onFetchMore={onFetchMore}
      hideLoader
      onRowClick={onRowClick}
      selectedRowKey={selectedOrganizationId}
      skeletonRowsCount={hasMore && isLoading ? 3 : 0}
    />
  )
}

OrganizationsTable.propTypes = {
  organizations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    coachesCount: PropTypes.number,
    certifiedCoachesCount: PropTypes.number,
    communitiesCount: PropTypes.number,
  })).isRequired,
  organizationsCount: PropTypes.number.isRequired,
  hasMore: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  isSortLoading: PropTypes.bool,
  sortOption: PropTypes.shape({ sortField: PropTypes.string, sortValue: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
  onFetchMore: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  selectedOrganizationId: PropTypes.number,
}

export default OrganizationsTable
